import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { AuthGuard } from './Guards/auth.guard';
import { PreviousOrderGuard } from './Guards/previous-order.guard';
import { MyRewardGuard } from './Guards/my-reward.guard';
import { ReferalGuard } from './Guards/referal.guard';
import { payGuard } from './Guards/pay.guard';
import { GuestGuard } from './Guards/guest.guard';
import { LoginGuard } from './Guards/login.guard';
import { DesignRouteGuard } from './design/design-route.guard';
import { DefaultPageGuard } from './brand/default-page.guard';
import { LegacyBookingRedirectGuard } from './bookings/legacy-booking-redirect.guard';
import { assertRouteRootsKnown } from './brand/brand-context';

/*
 * Pages with a redesigned twin carry DesignRouteGuard: with
 * TemplateData.NewDesign on they redirect to the twin (design/design-twins.ts),
 * otherwise they render as before. Their own guards move to
 * data.designThen so they run only when the page stays.
 *
 * Every page is mounted twice (see `routes` at the bottom): under the brand
 * flavour (`/<flavor>/menu`, the historical form that every old link, QR
 * code, email and embed uses) and bare (`/menu`) for a host that belongs to
 * one brand, where brand/brand-context.ts resolves the flavour from the host
 * name. Which form a page is on decides what RequestService.setUrl() /
 * brandUrl() produce, so links keep the form the visitor arrived with.
 * A page's first segment must be listed in ROUTE_ROOTS (brand-context.ts):
 * that list is how a bare first segment is told apart from a brand flavour.
 */
const PAGES: Routes = [
  // the bare brand URL (and the old /home): TemplateData.DefaultPage, else the menu
  { path: '', pathMatch: 'full', canActivate: [DefaultPageGuard], children: [] },
  { path: 'home', pathMatch: 'full', canActivate: [DefaultPageGuard], children: [] },

  //For Crown Epoxy
  {
    path: 'products',
    canActivate: [DesignRouteGuard], // bare /products → /shop
    // re-run on every navigation inside /products, so going from a child
    // page (product-details) back to the bare page still redirects
    runGuardsAndResolvers: 'always',
    loadChildren: () =>
      import('./products/products.module').then((m) => m.ProductsModule),
  },

  {
    path: 'events',
    loadChildren: () =>
      import('./events/events.module').then((m) => m.EventsModule),
  },

  // Renders the redesigned (AuthComponent) or legacy (LoginComponent) screen
  // by TemplateData.NewDesign — see LoginSwitchComponent. Same URL for both.
  {
    path: 'login',
    loadChildren: () =>
      import('./login-switch/login-switch.module').then(
        (m) => m.LoginSwitchModule
      ),
  },

  {
    path: 'menu',
    canActivate: [DesignRouteGuard],
    loadChildren: () => import('./menu/menu.module').then((m) => m.MenuModule),
  },
  {
    path: 'app-menu',
    canActivate: [DesignRouteGuard],
    loadChildren: () => import('./menu/menu.module').then((m) => m.MenuModule),
  },
  {
    path: 'new-menu',
    loadChildren: () =>
      import('./new-menu/new-menu.module').then((m) => m.NewMenuModule),
  },
  {
    path: 'app-new-menu',
    loadChildren: () =>
      import('./new-menu/new-menu.module').then((m) => m.NewMenuModule),
  },
  {
    path: 'dine-in',
    canActivate: [DesignRouteGuard],
    loadChildren: () =>
      import('./dineIn/dineIn.module').then((m) => m.DineInModule),
  },
  // QR code on a table: same page in ordering mode (TemplateData.DineIn.DineInOrders)
  {
    path: 'dine-in/:tableId',
    canActivate: [DesignRouteGuard],
    loadChildren: () =>
      import('./dineIn/dineIn.module').then((m) => m.DineInModule),
  },
  // redesigned dine-in menu (/dine-in redirects here with NewDesign on)
  {
    path: 'new-dine-in',
    loadChildren: () =>
      import('./new-dine-in/new-dine-in.module').then(
        (m) => m.NewDineInModule
      ),
  },
  {
    path: 'new-dine-in/:tableId',
    loadChildren: () =>
      import('./new-dine-in/new-dine-in.module').then(
        (m) => m.NewDineInModule
      ),
  },

  {
    path: 'join-reward',
    canActivate: [DesignRouteGuard], // → /rewards with NewDesign on
    loadChildren: () =>
      import('./reward/reward.module').then((m) => m.RewardModule),
  },

  {
    path: 'rewards',
    loadChildren: () =>
      import('./rewards/rewards.module').then((m) => m.RewardsModule),
  },

  {
    path: 'postcode-iframe',
    loadChildren: () =>
      import('./postcode-iframe/postcode-iframe.module').then(
        (m) => m.PostcodeIframeModule
      ),
  },

  // the booking page's old address (links in confirmation emails already
  // sent): redirect to /bookings with the query string. Listed before
  // `reservation` so the ReservationModule chunk is never downloaded for it.
  {
    path: 'reservation/service-booking',
    pathMatch: 'full',
    canActivate: [LegacyBookingRedirectGuard],
    children: [],
  },

  {
    path: 'reservation',
    canActivate: [DesignRouteGuard], // bare /reservation only
    runGuardsAndResolvers: 'always', // see /products
    loadChildren: () =>
      import('./reservation/reservation.module').then(
        (m) => m.ReservationModule
      ),
  },

  // service / barber booking (moved out of /reservation/service-booking, which redirects here)
  {
    path: 'bookings',
    loadChildren: () =>
      import('./bookings/bookings.module').then((m) => m.BookingsModule),
  },

  {
    path: 'reserve-iframe',
    loadChildren: () =>
      import('./reservation/reservation.module').then(
        (m) => m.ReservationModule
      ),
  },
  // redesigned reservation (+ verify-reservation); /reservation redirects
  // here with NewDesign on, the /reserve-iframe embed stays as it is
  {
    path: 'new-reservation',
    loadChildren: () =>
      import('./new-reservation/new-reservation.module').then(
        (m) => m.NewReservationModule
      ),
  },

  {
    path: 'updateaccount',
    canActivate: [PreviousOrderGuard],
    loadChildren: () =>
      import('./update-account/update-account.module').then(
        (m) => m.UpdateAccountModule
      ),
  },
  {
    path: 'delete-account',
    loadChildren: () =>
      import('./delete-account/delete-account.module').then(
        (m) => m.DeleteAccountModule
      ),
  },
  {
    path: 'forgot-password',
    loadChildren: () =>
      import('./forgot-password/forgot-password.module').then(
        (m) => m.ForgotPasswordModule
      ),
  },

  {
    path: 'my-rewards',
    canActivate: [DesignRouteGuard], // → redesigned twin with NewDesign on
    data: { designThen: [MyRewardGuard] },
    loadChildren: () =>
      import('./my-rewards/my-rewards.module').then((m) => m.MyRewardsModule),
  },

  {
    path: 'previous-order',
    canActivate: [PreviousOrderGuard],
    loadChildren: () =>
      import('./previous-order/previous-order.module').then(
        (m) => m.PreviousOrderModule
      ),
  },
  {
    path: 'checkout',
    loadChildren: () =>
      import('./checkout/checkout.module').then((m) => m.CheckoutModule),
    canActivate: [DesignRouteGuard],
    data: { designThen: [AuthGuard] },
  },
  {
    path: 'new-checkout',
    loadChildren: () =>
      import('./new-checkout/new-checkout.module').then(
        (m) => m.NewCheckoutModule
      ),
    canActivate: [AuthGuard],
  },
  {
    path: 'order-checkout',
    loadChildren: () =>
      import('./order-checkout/order-checkout.module').then(
        (m) => m.OrderCheckoutModule
      ),
    canActivate: [AuthGuard],
  },

  {
    path: 'terms-and-conditions',
    loadChildren: () =>
      import('./terms/terms.module').then((m) => m.TermsModule),
  },

  {
    path: 'privacy-policy',
    loadChildren: () =>
      import('./privacy-policy/privacy-policy.module').then(
        (m) => m.PrivacyPolicyModule
      ),
  },

  {
    path: 'guest',
    loadChildren: () =>
      import('./checkout/checkout.module').then((m) => m.CheckoutModule),
    canActivate: [DesignRouteGuard],
    data: { designThen: [GuestGuard] },
  },

  {
    path: 'new-guest-checkout',
    loadChildren: () =>
      import('./new-checkout/new-checkout.module').then(
        (m) => m.NewCheckoutModule
      ),
    canActivate: [GuestGuard],
  },

  {
    path: 'pay',
    loadChildren: () => import('./pay/pay.module').then((m) => m.PayModule),
    canActivate: [payGuard],
  },

  {
    path: 'giftcard',
    loadChildren: () =>
      import('./send-giftcard/send-giftcard.module').then(
        (m) => m.SendGiftcardModule
      ),
    canActivate: [DesignRouteGuard], // → redesigned twin with NewDesign on
    data: { designThen: [MyRewardGuard] },
  },

  {
    path: 'gift-cards',
    loadChildren: () =>
      import('./gift-cards/gift-cards.module').then(
        (m) => m.GiftCardsModule
      ),
    canActivate: [MyRewardGuard],
  },

  {
    path: 'branches',
    loadChildren: () =>
      import('./branches/branches.module').then((m) => m.BranchesModule),
  },

  {
    path: 'online',
    loadChildren: () =>
      import('src/app/online/online.module').then((m) => m.OnlineModule),
  },

  {
    path: 'shop',
    loadChildren: () => import('./shop/shop.module').then((m) => m.ShopModule),
  },

  {
    path: 'store',
    loadChildren: () =>
      import('src/app/store/store.module').then((m) => m.StoreModule),
  },

  {
    path: 'distributors',
    loadChildren: () =>
      import('src/app/store/store.module').then((m) => m.StoreModule),
  },

  {
    path: 'category-products/:catID',
    loadChildren: () =>
      import('./category-products/category-products.module').then(
        (m) => m.CategoryProductsModule
      ),
  },

  {
    path: 'refer',
    loadChildren: () =>
      import('./referal/referal.module').then((m) => m.ReferalModule),
    canActivate: [ReferalGuard],
  },

  {
    path: 'favourites',
    loadChildren: () =>
      import('./favourites/favourites.module').then((m) => m.FavouritesModule),
    canActivate: [LoginGuard],
  },

  {
    path: 'likeAndShare',
    loadChildren: () => import('./like/like.module').then((m) => m.LikeModule),
  },

  {
    path: 'stock-list',
    loadChildren: () =>
      import('src/app/car-listings/list/car-listing.module').then(
        (m) => m.CarlistingModule
      ),
  },

  {
    path: 'sold-cars',
    loadChildren: () =>
      import('src/app/car-listings/list/car-listing.module').then(
        (m) => m.CarlistingModule
      ),
  },
  {
    path: 'invitation',
    loadChildren: () =>
      import('src/app/invitations/invitations.module').then(
        (m) => m.InvitationsModule
      ),
  }
];

assertRouteRootsKnown(PAGES.map((r) => r.path));

const routes: Routes = [
  // brand host: /menu, /reservation/service-booking …
  { path: '', children: PAGES },
  // shared host, old links and embeds: /<flavor>/menu … (the router falls
  // through to this group when no bare page matched the first segment)
  { path: ':brand_name', children: PAGES },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
})
export class AppRoutingModule {}
